import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageParams, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IClientAccountInfiniteResponse,
  IClientAccountResponse,
  ICreateClientAccount,
  IUpdateClientAccount,
} from './types';

export const useGetClientAccounts = (params?: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();

  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IClientAccountInfiniteResponse[], DefaultError>({
    queryKey: ['client-accounts-infinite-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'ClientAccount/GetAccounts',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<
        IInfinitePageResponse<IClientAccountInfiniteResponse>
      >(response, {
        groupName: `useGetClientAccounts - ${pageParam}`,
      });

      return infiniteHandler<IClientAccountInfiniteResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

// export const useGetClientAccounts = () => {
//   const { responseHandler } = useResponseHandler();
//   return useQuery<IClientAccountResponse[], DefaultError>({
//     queryKey: ['client-accounts'],
//     async queryFn() {
//       const response = await apiClient({
//         method: 'GET',
//         url: `ClientAccount/GetAccounts`,
//         params: { itemCount: 999 },
//       });

//       return responseHandler(response, {
//         groupName: 'useGetClientAccounts',
//         notFoundPath: '/admin/client-accounts',
//       });
//     },
//   });
// };

export const useGetClientAccount = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IClientAccountResponse, DefaultError>({
    queryKey: ['client-account', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ClientAccount/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetClientAccount',
        notFoundPath: '/admin/client-accounts',
      });
    },
  });
};

export const useCreateClientAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useCreateClientAccount');
  return useMutation<
    IClientAccountResponse,
    DefaultError,
    ICreateClientAccount
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'ClientAccount/CreateAccount',
        body,
      });

      return responseHandler(response, {
        groupName: 'useCreateClientAccount',
        notFoundPath: '/admin/client-accounts',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};
export const useUpdateClientAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useUpdateClientAccount');
  return useMutation<
    IClientAccountResponse,
    DefaultError,
    IUpdateClientAccount
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'ClientAccount/UpdateAccount',
        body,
      });

      return responseHandler(response, {
        groupName: 'useUpdateClientAccount',
        notFoundPath: '/admin/client-accounts',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteClientAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteClientAccount');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (id: string) => {
      loading(i18next.t('service-code.delete.loading', { what: '' }));

      const response = await apiClient({
        method: 'DELETE',
        url: 'ClientAccount/DeleteAccount',
        body: { id: id },
      });

      return responseHandler(response, {
        groupName: `useDeleteClientAccount`,
      });
    },
    onSuccess: () => {
      success(i18next.t('service-message.remove.success', { what: '' }));
    },
    onError: dismiss,
  });
};
